import { useEffect, useState, useRef } from 'react';
import { Plus, Edit, Trash2, Image as ImageIcon, Eye, EyeOff, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../store/authStore';
import Modal from '../components/ui/Modal';
import Confirm from '../components/ui/Confirm';
import type { NewsItem } from '../types/database';

interface NewsForm {
  title: string;
  summary: string;
  body: string;
  image_url: string;
  instagram_url: string;
  youtube_url: string;
  facebook_url: string;
  tiktok_url: string;
  external_url: string;
  published: boolean;
}

const FORM_EMPTY: NewsForm = {
  title: '',
  summary: '',
  body: '',
  image_url: '',
  instagram_url: '',
  youtube_url: '',
  facebook_url: '',
  tiktok_url: '',
  external_url: '',
  published: true,
};

export default function NoticiasPage() {
  const { profile } = useAuthStore();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState<NewsItem | null>(null);
  const [form, setForm] = useState<NewsForm>(FORM_EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // Borrar y publicar/despublicar son acciones de fila: recargaban el listado
  // sin mirar si la escritura había entrado, así que un rechazo se veía como
  // "no ha pasado nada".
  const [listError, setListError] = useState('');
  // El guardado del modal cerraba y recargaba sin mirar su resultado: publicar
  // una noticia que la base rechazaba se veía como publicarla.
  const [modalError, setModalError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<NewsItem | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // «Aún no hay noticias. Crea la primera…» es una afirmación CON
  // INSTRUCCIÓN: desde ahí se publica, y publicar la que ya existe deja dos
  // noticias iguales en el muro de todos los alumnos.
  const [errorCarga, setErrorCarga] = useState<string | null>(null);

  useEffect(() => {
    loadNews();
  }, []);

  async function loadNews() {
    setLoading(true);
    const { data, error: errorLectura } = await supabase
      .from('news')
      .select('*')
      .order('published_at', { ascending: false });
    if (errorLectura) {
      setErrorCarga(errorLectura.message || 'Error desconocido');
      setNews([]);
      setLoading(false);
      return;
    }
    setErrorCarga(null);
    setNews((data as NewsItem[]) ?? []);
    setLoading(false);
  }

  function openCreate() {
    setEditTarget(null);
    setForm(FORM_EMPTY);
    setUploadError('');
    setModal(true);
  }

  function openEdit(item: NewsItem) {
    setEditTarget(item);
    setForm({
      title: item.title,
      summary: item.summary ?? '',
      body: item.body,
      image_url: item.image_url ?? '',
      instagram_url: item.instagram_url ?? '',
      youtube_url: item.youtube_url ?? '',
      facebook_url: item.facebook_url ?? '',
      tiktok_url: item.tiktok_url ?? '',
      external_url: item.external_url ?? '',
      published: item.published,
    });
    setUploadError('');
    setModal(true);
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    setUploading(true);
    const ext = file.name.split('.').pop();
    const path = `news/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('vertex-assets').upload(path, file);
    if (error) {
      setUploadError(`Error al subir: ${error.message}`);
      setUploading(false);
      return;
    }
    const { data } = supabase.storage.from('vertex-assets').getPublicUrl(path);
    setForm((f) => ({ ...f, image_url: data.publicUrl }));
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function handleSave() {
    if (!form.title.trim() || !form.body.trim()) {
      setUploadError('Título y texto son obligatorios');
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      summary: form.summary.trim() || null,
      body: form.body.trim(),
      image_url: form.image_url || null,
      instagram_url: form.instagram_url.trim() || null,
      youtube_url: form.youtube_url.trim() || null,
      facebook_url: form.facebook_url.trim() || null,
      tiktok_url: form.tiktok_url.trim() || null,
      external_url: form.external_url.trim() || null,
      published: form.published,
    };
    const { error } = editTarget
      ? await supabase.from('news').update(payload).eq('id', editTarget.id)
      : await supabase.from('news').insert({ ...payload, created_by: profile?.id });
    setSaving(false);
    if (error) {
      setModalError(`No se pudo guardar la noticia: ${error.message}`);
      return;
    }
    setModalError('');
    setModal(false);
    await loadNews();
  }

  async function handleDelete(item: NewsItem) {
    const { error } = await supabase.from('news').delete().eq('id', item.id);
    setConfirmDelete(null);
    if (error) {
      setListError(`No se pudo eliminar "${item.title}": ${error.message}`);
      return;
    }
    setListError('');
    await loadNews();
  }

  async function togglePublished(item: NewsItem) {
    const { error } = await supabase
      .from('news')
      .update({ published: !item.published })
      .eq('id', item.id);
    // Sin esto, despublicar una noticia rechazada por la base recargaba el
    // listado y la noticia seguía publicada, sin explicación.
    if (error) {
      setListError(
        `No se pudo ${item.published ? 'despublicar' : 'publicar'} "${item.title}": ${error.message}`
      );
      return;
    }
    setListError('');
    await loadNews();
  }

  return (
    <div className="space-y-6">
      {listError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="noticias-list-error"
        >
          {listError}
        </p>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-oswald text-2xl uppercase tracking-wider text-white">
            Noticias del club
          </h2>
          <p className="text-text-secondary text-sm">
            Muro visible en la app móvil de los alumnos.
          </p>
        </div>
        <button onClick={openCreate} className="btn-gold flex items-center gap-2">
          <Plus size={16} /> Nueva noticia
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
        </div>
      ) : errorCarga ? (
        <div className="card text-center py-12" data-testid="noticias-ilegibles">
          <p className="text-error text-sm">{errorCarga}</p>
          <p className="text-text-secondary text-sm mt-2">
            No dé el muro por vacío: publicar ahora podría duplicar una noticia que ya está
            publicada, y la verían todos los alumnos dos veces.
          </p>
          <button
            className="btn-secondary mt-4"
            onClick={loadNews}
            data-testid="noticias-reintentar"
          >
            Reintentar
          </button>
        </div>
      ) : news.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-text-secondary">
            Aún no hay noticias. Crea la primera para que aparezca en el muro de los alumnos.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {news.map((item) => (
            <div key={item.id} className="card flex gap-4">
              {item.image_url ? (
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-32 h-32 object-cover rounded"
                />
              ) : (
                <div className="w-32 h-32 rounded bg-bg-elevated flex items-center justify-center">
                  <ImageIcon size={24} className="text-text-secondary" />
                </div>
              )}
              <div className="flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-oswald text-lg text-white">{item.title}</h3>
                  <span className={item.published ? 'badge-success' : 'badge-warning'}>
                    {item.published ? 'Publicada' : 'Oculta'}
                  </span>
                </div>
                <p className="text-text-secondary text-xs mt-1 flex items-center gap-1">
                  <Calendar size={11} /> {format(new Date(item.published_at), 'dd/MM/yyyy HH:mm')}
                </p>
                {item.summary && (
                  <p className="text-text-secondary text-sm mt-2 line-clamp-2">{item.summary}</p>
                )}
                <div className="flex items-center gap-2 mt-auto pt-3">
                  <button
                    onClick={() => openEdit(item)}
                    className="btn-ghost flex items-center gap-1 text-sm"
                  >
                    <Edit size={13} /> Editar
                  </button>
                  <button
                    onClick={() => togglePublished(item)}
                    className="btn-ghost flex items-center gap-1 text-sm"
                  >
                    {item.published ? (
                      <>
                        <EyeOff size={13} /> Ocultar
                      </>
                    ) : (
                      <>
                        <Eye size={13} /> Publicar
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(item)}
                    className="btn-ghost flex items-center gap-1 text-sm text-error"
                  >
                    <Trash2 size={13} /> Borrar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editTarget ? 'Editar noticia' : 'Nueva noticia'}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="label">Título *</label>
            <input
              className="input"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </div>

          <div>
            <label className="label">Resumen (1-2 frases, aparece en la tarjeta de la app)</label>
            <input
              className="input"
              value={form.summary}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
            />
          </div>

          <div>
            <label className="label">Texto completo *</label>
            <textarea
              className="input min-h-[120px]"
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              placeholder="Contenido completo de la noticia..."
            />
          </div>

          <div>
            <label className="label">Imagen</label>
            <div className="flex items-center gap-3">
              {form.image_url ? (
                <img src={form.image_url} alt="" className="w-24 h-24 object-cover rounded" />
              ) : (
                <div className="w-24 h-24 rounded bg-bg-elevated flex items-center justify-center">
                  <ImageIcon size={20} className="text-text-secondary" />
                </div>
              )}
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="text-sm text-text-secondary"
                />
                {form.image_url && (
                  <button
                    onClick={() => setForm((f) => ({ ...f, image_url: '' }))}
                    className="btn-ghost text-xs"
                  >
                    Eliminar imagen
                  </button>
                )}
                {uploading && <span className="text-text-secondary text-xs">Subiendo...</span>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Instagram</label>
              <input
                className="input"
                value={form.instagram_url}
                onChange={(e) => setForm((f) => ({ ...f, instagram_url: e.target.value }))}
                placeholder="https://instagram.com/..."
              />
            </div>
            <div>
              <label className="label">YouTube</label>
              <input
                className="input"
                value={form.youtube_url}
                onChange={(e) => setForm((f) => ({ ...f, youtube_url: e.target.value }))}
                placeholder="https://youtube.com/..."
              />
            </div>
            <div>
              <label className="label">Facebook</label>
              <input
                className="input"
                value={form.facebook_url}
                onChange={(e) => setForm((f) => ({ ...f, facebook_url: e.target.value }))}
                placeholder="https://facebook.com/..."
              />
            </div>
            <div>
              <label className="label">TikTok</label>
              <input
                className="input"
                value={form.tiktok_url}
                onChange={(e) => setForm((f) => ({ ...f, tiktok_url: e.target.value }))}
                placeholder="https://tiktok.com/..."
              />
            </div>
            <div className="col-span-2">
              <label className="label">Enlace adicional</label>
              <input
                className="input"
                value={form.external_url}
                onChange={(e) => setForm((f) => ({ ...f, external_url: e.target.value }))}
                placeholder="Web, blog, otro..."
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
              className="w-4 h-4 accent-gold-primary"
            />
            <span className="text-white text-sm">Publicar inmediatamente</span>
          </label>

          {uploadError && <p className="text-error text-sm">{uploadError}</p>}
          {modalError && (
            <p className="text-error text-sm" data-testid="noticias-modal-error">
              {modalError}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={() => setModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving || uploading || !form.title.trim() || !form.body.trim()}
              className="btn-gold"
            >
              {saving ? 'Guardando...' : editTarget ? 'Guardar cambios' : 'Crear noticia'}
            </button>
          </div>
        </div>
      </Modal>

      <Confirm
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        title="Borrar noticia"
        message={`¿Borrar la noticia "${confirmDelete?.title}"? Esta acción no se puede deshacer.`}
        confirmLabel="Borrar"
        danger
      />
    </div>
  );
}
